import {useClaims} from "Hooks/api/useClaims";
import {useMemo, useState} from "react";
import {PROMO_PREFIXES} from "Constants";
import extendedDayjs from "Helpers/dayjs";
import {_useUnclaimedPromotions} from "Hooks/api/usePromotions";
import {
    GAMBIT_DOUBLE_WHEEL_MODAL_ID,
    GAMBIT_MATCH_THREE_MODAL_ID,
    GAMBIT_PENALTY_SHOOTOUT_MODAL_ID,
    GAMBIT_SINGLE_WHEEL_MODAL_ID,
    IS_DEV,
    IS_DOUBLE_WHEEL_ENABLED,
    IS_MATCH_THREE_ENABLED,
    IS_PENALTY_SHOOTOUT_ENABLED,
    IS_SINGLE_WHEEL_ENABLED,
    SHARED_CDN_PATH
} from "Setup/config";
import {sleep} from "Helpers/async";
import useGames from "Hooks/useGames";

export const MATCH_THREE_CARDS = "MatchThreeCards";
export const DOUBLE_WHEEL = "DoubleWheel";
export const SINGLE_WHEEL = "SingleWheel";
export const PENALTY_SHOOTOUT = "PenaltyShootout";

const GAMIFICATION_ASSETS_PATH = `${SHARED_CDN_PATH}/Gamification/%GAME%/`;
const GAMIFICATION_IMAGE_PATH = `${GAMIFICATION_ASSETS_PATH}webp/`;

const setAssetsPathForGamification = (game) => GAMIFICATION_ASSETS_PATH.replace('%GAME%', game);
const setImagesPathForGamification = (game) => GAMIFICATION_IMAGE_PATH.replace('%GAME%', game);

const gambitGameConfigSkins = {
    [MATCH_THREE_CARDS]: "SantasWorkshop",
    [DOUBLE_WHEEL]: "DoubleWheel",
    [SINGLE_WHEEL]: "SingleWheel",
    [PENALTY_SHOOTOUT]: "PenaltyShootout",
};

const getGambitGameConfig = (configId) => {
    const defaultSettings = {
        id: configId,
        hasAudio: false,
        setGameClaimOnPlay: true,
        gameClaimName: configId,
    };

    const configs = {
        [MATCH_THREE_CARDS]: {
            enabled: IS_MATCH_THREE_ENABLED,
            hasAudio: true,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_MATCH_THREE_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_MATCH_THREE_GAME_PRIZE,
            prizeSymbols: ["❄️", "🎄", "🎅"],
            modalId: GAMBIT_MATCH_THREE_MODAL_ID
        },
        [DOUBLE_WHEEL]: {
            enabled: IS_DOUBLE_WHEEL_ENABLED,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_DOUBLE_WHEEL_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_DOUBLE_WHEEL_GAME_PRIZE,
            modalId: GAMBIT_DOUBLE_WHEEL_MODAL_ID
        },
        [SINGLE_WHEEL]: {
            enabled: IS_SINGLE_WHEEL_ENABLED,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_SINGLE_WHEEL_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_SINGLE_WHEEL_GAME_PRIZE,
            modalId: GAMBIT_SINGLE_WHEEL_MODAL_ID
        },
        [PENALTY_SHOOTOUT]: {
            enabled: IS_PENALTY_SHOOTOUT_ENABLED,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_PENALTY_SHOOTOUT_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_PENALTY_SHOOTOUT_GAME_PRIZE,
            modalId: GAMBIT_PENALTY_SHOOTOUT_MODAL_ID
        },
    }

    const chosenConfig = configs?.[configId];

    if (chosenConfig) {
        return {
            ...defaultSettings,
            ...chosenConfig
        };
    }

    return null;
}

export const useGambitGameV2 = (game = null) => {
    const isTestMode = IS_DEV;

    const TODAY = extendedDayjs().format("DD-MM-YYYY");
    const GAMBIT_GAMES_CLAIM = "GambitGamesState";
    const GAME_CONFIG = getGambitGameConfig(game);
    const GAME_CLAIM_NAME = GAME_CONFIG?.gameClaimName ?? null;

    // Useful dev state to mock the initial deposit screen
    const [canPlayGambitGame_DEV, setCanPlayGambitGame_DEV] = useState(false);
    const [isMasterGroupBankingInProgress, setIsMasterGroupBankingInProgress] = useState(false);
    const [showEndGameMessage, setShowEndGameMessage] = useState(false);
    const {unclaimedPromotions, unclaimedPromotionsLoaded} = _useUnclaimedPromotions();

    const {_make, _update, playerClaimsLoaded, playerClaims} = useClaims();
    const {makePlayerClaim} = _make();
    const {updatePlayerClaim} = _update();
    const {findGameByCatalogueGameId} = useGames();

    const launchMasterGroupBanking = async () => {
        setIsMasterGroupBankingInProgress(true);

        if (isTestMode) {
            await sleep(3000);
            setCanPlayGambitGame_DEV(true);
        }
    };

    const mastergroupBonus = useMemo(() => {
        if (!GAME_CONFIG) return null;
        if (!unclaimedPromotionsLoaded) return false;

        return unclaimedPromotions.find(promo => {
            try {
                const internalName = promo.internalName.toUpperCase();
                return internalName.startsWith(GAME_CONFIG.mastergroupBonus);
            } catch (err) {
                console.log(err);
            }
        })
    }, [GAME_CONFIG, unclaimedPromotions, unclaimedPromotionsLoaded]);

    const gameSkinId = useMemo(() => {
        if (!mastergroupBonus) return gambitGameConfigSkins?.[game];

        if (mastergroupBonus?.customFields) {
            const skinId = mastergroupBonus.customFields.find(cf => cf.key === "SkinId");
            if (skinId) {
                return skinId?.value;
            }
        }

        return gambitGameConfigSkins?.[game];
    }, [mastergroupBonus]);

    const prizeBonus = useMemo(() => {
        if (!GAME_CONFIG) return null;
        if (!unclaimedPromotionsLoaded) return false;

        return unclaimedPromotions.find(p => p.internalName.toUpperCase().includes(GAME_CONFIG.prizeBonus));
    }, [GAME_CONFIG, unclaimedPromotions, unclaimedPromotionsLoaded])

    const setGamePlayedClaim = () => {
        // if (isTestMode) return;
        if (!GAME_CONFIG.setGameClaimOnPlay) return;
        if (!playerClaimsLoaded) return;

        const existingClaim = playerClaims.find(claim => {
            return claim.type === GAMBIT_GAMES_CLAIM;
        });

        if (existingClaim) {
            try {
                let parsedClaimValue = JSON.parse(existingClaim.value);

                if (!parsedClaimValue?.[GAME_CLAIM_NAME]) {
                    parsedClaimValue[GAME_CLAIM_NAME] = {};
                }

                parsedClaimValue[GAME_CLAIM_NAME].lastPlayed = TODAY;

                updatePlayerClaim(existingClaim.id, JSON.stringify(parsedClaimValue));
            } catch (e) {
                console.log(e);
            }
        } else {
            let claimValue = {};
            claimValue[GAME_CLAIM_NAME] = {};
            claimValue[GAME_CLAIM_NAME].lastPlayed = TODAY;

            makePlayerClaim(GAMBIT_GAMES_CLAIM, JSON.stringify(claimValue));
        }
    };

    const canViewGame = useMemo(() => {
        if (!GAME_CONFIG || !GAME_CONFIG?.enabled) return null;
        if (!playerClaimsLoaded) return false;
        if (!mastergroupBonus || !prizeBonus) return false;

        const existingClaim = playerClaims.find(claim => {
            return claim.type === GAMBIT_GAMES_CLAIM;
        });

        if (existingClaim?.value) {
            const parsedClaimValue = JSON.parse(existingClaim.value);
            if (parsedClaimValue?.[GAME_CLAIM_NAME]?.lastPlayed === TODAY) return false;
        }

        return true;
    }, [GAME_CONFIG, mastergroupBonus, prizeBonus, playerClaims, playerClaimsLoaded]);

    const canPlayGame = useMemo(() => {
        if (!canViewGame) return false;

        return mastergroupBonus && !mastergroupBonus?.canBeClaimed && prizeBonus?.canBeClaimed;
    }, [GAME_CONFIG, canViewGame, mastergroupBonus, prizeBonus]);

    const winningGameDataFromCustomFields = useMemo(() => {
        if (!prizeBonus) return null;

        if (prizeBonus?.customFields) {
            const gameId = prizeBonus.customFields.find(cf => cf.key === "GameIdLaunchAfterClaim");
            if(gameId?.value) {
                return findGameByCatalogueGameId(gameId.value);
            }
        }

        return null;
    }, [prizeBonus]);

    const assetsPath = useMemo(() => setAssetsPathForGamification(gameSkinId), [gameSkinId]);
    const imagesPath = useMemo(() => setImagesPathForGamification(gameSkinId), [gameSkinId]);

    return {
        isTestMode,
        gameConfig: {assetsPath, imagesPath, ...GAME_CONFIG},
        setGamePlayedClaim,
        setCanPlayGambitGame_DEV,
        canPlayGambitGame: isTestMode ? canPlayGambitGame_DEV : canPlayGame,
        canViewGambitGame: canViewGame,
        gambitGamePrizeBonus: prizeBonus,
        gambitGameMastergroupBonus: mastergroupBonus,
        isMasterGroupBankingInProgress,
        launchMasterGroupBanking,
        showEndGameMessage,
        setShowEndGameMessage,
        winningGameDataFromCustomFields
    };
};